<template>
  <div v-if="playerPerformance.length === 1">
    <section class="mt-5">
      <line-chart
        v-if="!loadingRawHeartRate"
        class="mb-5"
        :series-override="[{ name: $t('card_title.heart_rate'), data: heartRateLogSeries },{ name: $t('training_builder.text_input.speed.title'), data: speedSeries }]"
        :xaxis="{ type: 'datetime', labels: { datetimeUTC: false } }"
        :yaxis="[ { title: { text: $t('hr_variables.hr_bpm') }, max: 210, min: 0, seriesName: 'Heart Rate' }, { title: { text: $t('training_builder.groups.speed') + '(km/h)' }, min: 0, seriesName: 'Speed', opposite: true } ]"
        :tooltip="{
          x: {
            format: 'HH:mm:ss'
          }
        }"
        :animations="{enabled: false}"
        :colors="['#E91E63', '#008FFB']"
        :annotations="heartRateLogAnnotations"
        :height="500"
        :fill="{ type: 'solid', opacity: [1, 1] }"
      >
        <template v-slot:header>
          <v-card-title class="pa-6">
            <div class="subtitle-2 grey--text text--grey-100">
              {{ $t('card_title.heart_rate_log') }}
            </div>
          </v-card-title>
        </template>
      </line-chart>
      <v-card
        v-else
        class="rounded-lg"
      >
        <v-card-title class="pa-6">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.heart_rate_log') }}
          </div>
        </v-card-title>
        <v-card-text class="text-center py-16">
          <v-progress-circular
            color="grey lighten-1"
            :size="180"
            indeterminate
            :width="2"
          />
        </v-card-text>
      </v-card>
    </section>

    <section>
      <line-chart
        class="mb-5"
        :raw-data="playerPerformance"
        :chart-value-keys="[ { key: 'ZONE1DR', type: 'bar', label: '1.' + $t('hr_variables.regeneration') }, { key: 'ZONE2DR', type: 'bar', label: '2.' + $t('hr_variables.extensive_aerobic_zone') }, { key: 'ZONE3DR', type: 'bar', label: '3.' + $t('hr_variables.intensive_aerobic_zone') }, { key: 'ZONE4DR', type: 'bar', label: '4.' + $t('hr_variables.anaerobic_threshold_zone') }, { key: 'ZONE5DR', type: 'bar', label: '5.' + $t('hr_variables.vo2_max_zone') } ]"
        x-value="last_name"
        y-value-prefix="player_stats."
        :yaxis="[{min: 0}]"
        :xaxis="{
          labels: {
            rotate: -45
          },
          categories: playerPerformance.map(p => p.first_name + ' ' + p.last_name),
          tickPlacement: 'between'
        }"
        :colors="[ '#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560']"
        :markers="{ colors: [ '#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560']}"
        :fill="{ type: ['solid', 'solid', 'solid', 'solid', 'solid']}"
      >
        <template v-slot:header>
          <v-card-title class="pa-6">
            <div class="subtitle-2 grey--text text--grey-100">
              {{ $t("card_title.hr_zone_analysis") }}
            </div>
          </v-card-title>
        </template>
      </line-chart>
    </section>
  </div>
  <div v-else>
    <v-row
      class="mt-5"
    >
      <v-col
        sm="12"
        md="12"
        lg="12"
        xl="6"
      >
        <section>
          <line-chart
            class="mb-5"
            :raw-data="playerPerformance"
            :chart-value-keys="[ { key: 'HRMAX', label: $t('hr_variables.max_heart_rate'), type: 'line' }, { key: 'HRAVG', type: 'bar', label: $t('hr_variables.avg_heart_rate') } ]"
            x-value="last_name"
            y-value-prefix="player_stats."
            :yaxis="[ { title: { text: $t('hr_variables.hr_bpm') }, min: 0} ]"
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: playerPerformance.map(p => p.first_name + ' ' + p.last_name),
              tickPlacement: 'between'
            }"
            :colors="['#FF4560', '#df7595' ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.avg_max_heart_rate') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col
        sm="12"
        md="12"
        lg="12"
        xl="6"
      >
        <section>
          <line-chart
            class="mb-5"
            type="bar"
            :raw-data="playerPerformance"
            :chart-value-keys="[ { key: 'TRIMP', type: 'bar', label: 'TRIMP' } ]"
            x-value="last_name"
            y-value-prefix="player_stats."
            :yaxis="[ { title: { text: $t('hr_variables.trimp') }} ]"
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: playerPerformance.map(p => p.first_name + ' ' + p.last_name),
              tickPlacement: 'between'
            }"
            :colors="[ '#775DD0' ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('table_headers.trimp_training_impulse') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
    </v-row>
    <v-row
      class="mt-3"
    >
      <v-col
        cols="12"
      >
        <section>
          <line-chart
            class="mb-5"
            :raw-data="playerPerformance"
            :chart-value-keys="[ { key: 'ZONE1DR', type: 'bar', label: '1.' + $t('hr_variables.regeneration') }, { key: 'ZONE2DR', type: 'bar', label: '2.' + $t('hr_variables.extensive_aerobic_zone') }, { key: 'ZONE3DR', type: 'bar', label: '3.' + $t('hr_variables.intensive_aerobic_zone') }, { key: 'ZONE4DR', type: 'bar', label: '4.' + $t('hr_variables.anaerobic_threshold_zone') }, { key: 'ZONE5DR', type: 'bar', label: '5.' + $t('hr_variables.vo2_max_zone') } ]"
            x-value="last_name"
            y-value-prefix="player_stats."
            :yaxis="[{min: 0}]"
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: playerPerformance.map(p => p.first_name + ' ' + p.last_name),
              tickPlacement: 'between'
            }"
            :colors="[ '#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560']"
            :markers="{ colors: [ '#b3b3b3', '#008FFB', '#00E396', '#FEB019', '#FF4560']}"
            :fill="{ opacity: [0.5, 0.5, 0.5, 0.5, 0.5]}"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t("card_title.hr_zone_analysis") }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
      <v-col>
        <section>
          <line-chart
            class="mb-5"
            :raw-data="eventData.players"
            :chart-value-keys="[ { key: 'ACWRHRR', type: 'bar', label: $t('hr_variables.hr_reserve_acwr') }, { key: 'ACWRTRIMP', type: 'bar', label: $t('hr_variables.trimp_acwr') } ]"
            x-value="last_name"
            y-value-prefix="player_stats."
            :yaxis="[ { title: { text: $t('card_title.acwr') }} ]"
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: eventData.players.map(p => p.first_name + ' ' + p.last_name),
              tickPlacement: 'on'
            }"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('table_headers.acwr_hr_reserve_trimp') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
        </section>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart'
import dayjs from 'dayjs'
import i18n from '@/plugins/i18n'

export default {
  name: 'PerformanceHeartRateGraphs',
  components: { LineChart },
  props: {
    playerPerformance: {
      type: Array,
      required: true
    },
    eventData: {
      type: Object,
      required: true
    },
    loadingRawHeartRate: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    heartRateLogSeries() {
      if (this.playerPerformance.length !== 1) return undefined

      const data = this.playerPerformance[0].player_stats.HEART_RATE ?? []

      const timestamps = this.playerPerformance[0].player_stats.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.eventData.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }

      return series
    },
    speedSeries() {
      if (this.playerPerformance.length !== 1) return undefined

      const data = this.playerPerformance[0].player_stats.SPEED ?? []

      const timestamps = this.playerPerformance[0].player_stats.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.eventData.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }

      return series
    },
    heartRateLogAnnotations() {
      if (this.playerPerformance.length !== 1) return undefined

      const hrProfile = this.playerPerformance[0].hr_profile

      if (!hrProfile) return undefined

      const method = hrProfile.method

      let addKey = ''

      if (method === 'HRANP') addKey = 'ANPM'

      const zones = [
        { key: 'HRREG', color: '#bfbfbf', label: i18n.t('hr_variables.regeneration_zone') },
        { key: 'HRRE', color: '#008FFB', label: i18n.t('hr_variables.extensive_aerobic_zone') },
        { key: 'HRAI', color: '#00E396', label: i18n.t('hr_variables.intensive_aerobic_zone') },
        { key: 'HRANP', color: '#FEB019', label: i18n.t('hr_variables.anaerobic_threshold_zone') },
        { key: 'HRVO2MAX', color: '#FF4560', label: i18n.t('hr_variables.vo2_max_zone') }
      ]

      return {
        position: 'back',
        yaxis: zones.map(z => {
          return {
            y: hrProfile.calculations[z.key + addKey].lower,
            y2: hrProfile.calculations[z.key + addKey].upper,
            opacity: 0.6,
            fillColor: z.color,
            borderColor: z.color,
            label: {
              text: z.label,
              offsetY: 17,
              offsetX: -5,
              style: {
                background: 'transparent'
              }
            }
          }
        })
      }
    }
  }
}
</script>

<style scoped>

</style>
